import React, { useEffect, useState } from "react";

import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { decodeToken } from "react-jwt";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { PublicFooter } from "../../../components/PublicFooter";
import { AppConfig, PUBLIC_ROUTES } from "../../../constants";
import CompanyService from "../../../services/company.service";
import { ApprovedScreen } from "./components/ApprovedScreen";
import { ApprovingScreen } from "./components/ApprovingScreen";
import useStyles, { MainStack } from "./styles";

const LOGO = AppConfig.appLogo;

interface Decoded {
  id: number;
  name: string;
}

const ApproveCompany = () => {
  const history = useHistory();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  // eslint-disable-next-line no-restricted-globals
  const parsed: { token?: string } = queryString.parse(location.search);

  const [companyApproved, setCompanyApproved] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");

  const { mutate: approveCompanyMutation } = useMutation(
    ["company.approve"],
    (id: number) => CompanyService.approve(id),
    {
      onSuccess: () => {
        NotificationToast.success(translate("theCompanyHasBeenApproved"));
        setCompanyApproved(true);
      },
      onError: (error: unknown) => {
        const errorAxios = error as AxiosError<Error>;
        const message = errorAxios?.response?.data?.message;

        NotificationToast.error(
          message && typeof message === "string" ? message : translate("anErrorOccurredTryAgain"),
          { id: "react.query.default.error" }
        );
        history.replace("/");
      },
    }
  );

  useEffect(() => {
    if (parsed?.token) {
      setToken(parsed.token);
    } else {
      history.replace(PUBLIC_ROUTES.SIGN_IN);
    }
  }, [parsed]);

  useEffect(() => {
    if (token && !companyApproved) {
      const decodedToken = decodeToken<Decoded>(token);

      if (decodedToken) {
        setCompanyName(decodedToken?.name);
        approveCompanyMutation(decodedToken?.id);
      }
    }
  }, [token]);

  return (
    <div className={classes.container}>
      <MainStack direction="column" alignItems="center" justifyContent="center" spacing={3}>
        <Box
          borderRadius="12px"
          boxShadow="2px 1px 5px rgba(152, 162, 179, 0.15), 0px 4px 10px rgba(48, 140, 219, 0.05);"
          width="100%"
          height="100%"
          overflow="hidden"
        >
          <Grid container height="inherit">
            {companyApproved ? (
              <ApprovedScreen companyName={companyName} logoSrc={LOGO} />
            ) : (
              <ApprovingScreen logoSrc={LOGO  } />
            )}
          </Grid>
        </Box>
        <PublicFooter />
      </MainStack>
    </div>
  );
};

export default ApproveCompany;
